/**
 * proposalBlocks.tsx — Componentes web dos blocos de proposta
 *
 * Cada bloco reaproveita os componentes já existentes da landing pública, de
 * modo que a saída visual seja idêntica à tela atual. Nenhuma regra comercial
 * vive aqui: os valores vêm do snapshot devolvido pela Edge Function.
 *
 * RELEASE: v1.0.0 - 2026-09-09 - Versão inicial (Studio, Fase 2)
 */

import React, { useMemo } from 'react';
import { CheckCircle2, Clock, ShieldCheck, XCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ProposalHero } from '@/components/public-proposal/ProposalHero';
import { ProposalItemsList } from '@/components/public-proposal/ProposalItemsList';
import { ProposalTotals } from '@/components/public-proposal/ProposalTotals';
import { ProposalDecisionBar } from '@/components/public-proposal/ProposalDecisionBar';
import {
  daysUntil,
  formatDate,
  formatDateTime,
} from '@/components/public-proposal/proposalFormat';
import { useStudioWeb } from './StudioWebContext';
import type { StudioWebBlockComponent } from './types';

/** Apresentação (hero) com valor em destaque e CTAs primários. */
export const ProposalHeroBlock: StudioWebBlockComponent = () => {
  const { proposal, actions } = useStudioWeb();
  if (!proposal) return null;
  return (
    <ProposalHero
      data={proposal}
      onAccept={() => actions.onAccept?.()}
      onReject={() => actions.onReject?.()}
      onDownloadPdf={() => actions.onDownloadPdf?.()}
    />
  );
};

/** Faixa de situação final (aceita, recusada ou expirada). */
export const ProposalStatusBlock: StudioWebBlockComponent = () => {
  const { proposal } = useStudioWeb();
  if (!proposal) return null;
  const p = proposal.proposal;

  if (p.status === 'accepted') {
    return (
      <div className="proposal-surface proposal-reveal flex items-start gap-3 border-l-4 border-l-emerald-500 p-5">
        <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-emerald-500" />
        <div>
          <p className="text-sm font-semibold">Proposta aceita</p>
          <p className="text-xs text-muted-foreground">
            {p.accepted_by_name ? `Por ${p.accepted_by_name} · ` : ''}
            {formatDateTime(p.accepted_at)}
          </p>
        </div>
      </div>
    );
  }

  if (p.status === 'rejected') {
    return (
      <div className="proposal-surface proposal-reveal flex items-start gap-3 border-l-4 border-l-destructive p-5">
        <XCircle className="mt-0.5 h-5 w-5 shrink-0 text-destructive" />
        <div>
          <p className="text-sm font-semibold">Proposta recusada</p>
          <p className="text-xs text-muted-foreground">{formatDateTime(p.rejected_at)}</p>
        </div>
      </div>
    );
  }

  if (p.status === 'expired') {
    return (
      <div className="proposal-surface proposal-reveal flex items-start gap-3 border-l-4 border-l-amber-500 p-5">
        <Clock className="mt-0.5 h-5 w-5 shrink-0 text-amber-500" />
        <div>
          <p className="text-sm font-semibold">Esta proposta expirou</p>
          <p className="text-xs text-muted-foreground">
            Validade encerrada em {formatDate(p.expires_at)}. Solicite uma nova versão ao
            responsável comercial.
          </p>
        </div>
      </div>
    );
  }

  return null;
};

/** Lista de itens (snapshot congelado). */
export const ProposalItemsBlock: StudioWebBlockComponent = () => {
  const { proposal } = useStudioWeb();
  if (!proposal) return null;
  return (
    <ProposalItemsList items={proposal.items} currency={proposal.proposal.currency || 'BRL'} />
  );
};

/** Resumo financeiro. */
export const ProposalTotalsBlock: StudioWebBlockComponent = () => {
  const { proposal } = useStudioWeb();
  if (!proposal) return null;
  const p = proposal.proposal;
  return (
    <ProposalTotals
      subtotal={p.subtotal}
      discountTotal={p.discount_total}
      mrrTotal={p.mrr_total}
      total={p.total}
      currency={p.currency || 'BRL'}
    />
  );
};

/** Condições comerciais (HTML já sanitizado na criação da proposta). */
export const ProposalConditionsBlock: StudioWebBlockComponent = ({ node }) => {
  const { proposal } = useStudioWeb();
  if (!proposal?.proposal.conditions_html) return null;
  const titulo = (node.props.title as string) || 'Condições comerciais';
  return (
    <section className="proposal-surface proposal-reveal p-5 md:p-7">
      <h2 className="mb-3 text-base font-semibold">{titulo}</h2>
      <div
        className="prose prose-sm max-w-none text-sm text-muted-foreground"
        dangerouslySetInnerHTML={{ __html: proposal.proposal.conditions_html }}
      />
    </section>
  );
};

/** Faixa de fechamento com aceitar/recusar. */
export const ProposalDecisionBlock: StudioWebBlockComponent = () => {
  const { proposal, actions } = useStudioWeb();
  const remainingDays = useMemo(
    () => daysUntil(proposal?.proposal.expires_at),
    [proposal?.proposal.expires_at],
  );
  if (!proposal?.proposal.is_actionable) return null;
  return (
    <ProposalDecisionBar
      total={proposal.proposal.total}
      currency={proposal.proposal.currency || 'BRL'}
      remainingDays={remainingDays}
      onAccept={() => actions.onAccept?.()}
      onReject={() => actions.onReject?.()}
    />
  );
};

/** Botão isolado de aceite (usado em modelos personalizados). */
export const ProposalAcceptBlock: StudioWebBlockComponent = ({ node }) => {
  const { proposal, actions } = useStudioWeb();
  if (!proposal?.proposal.is_actionable) return null;
  return (
    <Button size="lg" onClick={() => actions.onAccept?.()}>
      {(node.props.label as string) || 'Aceitar proposta'}
    </Button>
  );
};

/** Botão isolado de recusa (usado em modelos personalizados). */
export const ProposalRejectBlock: StudioWebBlockComponent = ({ node }) => {
  const { proposal, actions } = useStudioWeb();
  if (!proposal?.proposal.is_actionable) return null;
  return (
    <Button size="lg" variant="outline" onClick={() => actions.onReject?.()}>
      {(node.props.label as string) || 'Recusar'}
    </Button>
  );
};

/** Rodapé com identificação do emissor. */
export const ProposalFooterBlock: StudioWebBlockComponent = () => {
  const { proposal } = useStudioWeb();
  if (!proposal) return null;
  return (
    <footer className="flex flex-col items-center gap-1.5 pt-2 text-center">
      <span className="inline-flex items-center gap-1.5 text-[11px] text-muted-foreground">
        <ShieldCheck className="h-3.5 w-3.5" />
        Documento gerado eletronicamente por {proposal.tenant.name}
      </span>
      <span className="text-[11px] text-muted-foreground/70">
        {proposal.proposal.proposal_number} · versão {proposal.proposal.version_number}
      </span>
    </footer>
  );
};
